import { FunctionComponent, RefObject } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState } from 'preact/hooks';
import { MoreVideosButton } from '../moreVideosButton/moreVideosButton';
import { useStyles } from '../../../common/hooks/useStyles';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { getGalleryPlaylistViewStyleObject } from './galleryPlaylistView.style';
import { css } from '../../../common/styles/styles.utils';
import { COMMON_ICON_TYPES } from '../../../common/components/icon/iconMapper';
import { Icon } from '../../../common/components/icon/icon';
import { setShowGalleryView, setUserInteraction } from '../store/moreVideos.actions';
import { VideoData } from '../../../common/store/store.types';
import { mediaDataSelector } from '../../../common/store/selectors/mediaData.selector';
import { useResize } from '../../../common/hooks/useResize';
import {
  GALLERY_VIDEO_PLACEHOLDER_EXTRA_LARGE_WIDTH,
  GALLERY_VIDEO_PLACEHOLDER_LARGE_WIDTH,
} from '../moreVideos.constants';
import { GalleryView } from './galleryView/galleryView';
import { getLargeGalleryCloseButtonId } from '../htmlElements/htmlElementsIdGetter';

const CLOSE_ARIA_LABEL = 'Close more videos';

interface GalleryPlaylistViewVariablesProps {
  setExitGalleryView?: (value: boolean) => void;
  updatedVideoList: Array<VideoData>;
}

export const GalleryPlaylistView: FunctionComponent<GalleryPlaylistViewVariablesProps> = props => {
  const { setExitGalleryView, updatedVideoList } = props;
  const dispatch = useDispatch();
  const playerSize = useSelector(playerDataSelector.playerSize);
  const showControls = useSelector(playerDataSelector.showControls);
  const videoList = useSelector(mediaDataSelector.videoList);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);

  const [numberOfVideoSpaces, setNumberOfVideoSpaces] = useState(0);

  const galleryViewContainerRef: RefObject<HTMLDivElement> = useRef(null);
  const isEndState = videoList.length === 1 && videoTagStatus === 'complete';
  const largePlayerSize = playerSize === 'large';

  const styles = useStyles(getGalleryPlaylistViewStyleObject, [playerSize, showControls, isEndState]);
  const { galleryViewContainerStyle, controlsWrapperStyle, closeIconStyle } = styles;

  useResize(() => {
    const currentWidth = galleryViewContainerRef.current?.clientWidth;
    if (largePlayerSize) {
      setNumberOfVideoSpaces(currentWidth / GALLERY_VIDEO_PLACEHOLDER_LARGE_WIDTH);
    } else {
      setNumberOfVideoSpaces(currentWidth / GALLERY_VIDEO_PLACEHOLDER_EXTRA_LARGE_WIDTH);
    }
  }, [largePlayerSize, galleryViewContainerRef]);

  const onClickCloseIcon = (): void => {
    setExitGalleryView(true);
    dispatch(setUserInteraction('moreVideosClosed'));
    dispatch(setShowGalleryView(false));
  };

  return (
    <div
      style={css(galleryViewContainerStyle)}
      ref={galleryViewContainerRef}
      onClick={(e): void => e.stopPropagation()}
    >
      <div style={controlsWrapperStyle}>
        <MoreVideosButton insideGallery setExitGalleryView={setExitGalleryView} />
        <Icon
          id={getLargeGalleryCloseButtonId(playerUniqueId)}
          icon={COMMON_ICON_TYPES.CLOSE_FILL}
          style={css(closeIconStyle)}
          onClick={onClickCloseIcon}
          ariaLabel={CLOSE_ARIA_LABEL}
        />
      </div>
      <GalleryView
        numberOfVideoSpaces={numberOfVideoSpaces}
        updatedVideoList={updatedVideoList}
      />
    </div>
  );
};
